<template>
  <v-dialog :value="value" persistent max-width="1200px" scrollable>
    <v-card v-if="activeModelId && model">
      <v-card-title>
        <span class="headline">{{
          $t("nav_buttons.user_action_history")
        }}</span>
        <v-spacer></v-spacer>
        <v-btn-toggle
          v-model="historyView"
          mandatory
          dense
          class="mr-2"
        >
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn value="table" small v-bind="attrs" v-on="on">
                <v-icon small>mdi-table</v-icon>
              </v-btn>
            </template>
            <span>{{ translate("table_view") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn value="timeline" small v-bind="attrs" v-on="on">
                <v-icon small>mdi-timeline-text-outline</v-icon>
              </v-btn>
            </template>
            <span>{{ translate("timeline_view") }}</span>
          </v-tooltip>
        </v-btn-toggle>
        <v-btn icon @click="close()">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-data-table
          v-show="historyView === 'table'"
          :loading="loading"
          :headers="headers"
          :items="items"
          :footer-props="{
            'items-per-page-options': dataTableOptions,
          }"
          :items-per-page="itemsPerPage"
          :page.sync="currentPage"
          @pagination="onPagination"
          :server-items-length="totalItems"
          class="elevation-1"
        >
          <template v-slot:item.changes_label="{ item }">
            <v-expansion-panels v-if="isset(item.changes)" class="ma-2">
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <span v-html="item.changes_title_label"></span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div v-html="item.changes_label"></div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </template>
        </v-data-table>

        <div v-show="historyView === 'timeline'">
          <v-progress-linear
            v-if="loading"
            indeterminate
            color="primary"
            class="mb-2"
          />
          <div
            v-else-if="items.length"
            class="horizontal-history-timeline overflow-x-auto py-2"
          >
            <div class="horizontal-history-timeline__strip d-flex flex-nowrap">
              <div
                v-for="(item, index) in items"
                :key="item.id || item.serial"
                class="horizontal-history-timeline__step flex-shrink-0"
              >
                <div
                  class="horizontal-history-timeline__axis d-flex align-center"
                >
                  <div
                    v-if="index > 0"
                    class="horizontal-history-timeline__line flex-grow-1"
                  />
                  <v-avatar color="primary" size="16" class="flex-shrink-0" />
                  <div
                    v-if="index < items.length - 1"
                    class="horizontal-history-timeline__line flex-grow-1"
                  />
                </div>
                <div
                  class="caption font-weight-medium text-center mt-1 px-1"
                >
                  {{ item.date_label }}
                </div>
                <v-card
                  outlined
                  class="elevation-1 mt-2 horizontal-history-timeline__card"
                >
                  <v-card-title class="subtitle-2 py-2 flex-column align-stretch">
                    <span class="align-self-start">{{ item.user_label }}</span>
                    <v-chip
                      x-small
                      label
                      color="primary"
                      outlined
                      class="mt-1 align-self-start"
                    >
                      {{ item.operation_label }}
                    </v-chip>
                  </v-card-title>
                  <v-card-text class="pt-0 pb-2">
                    <v-expansion-panels
                      v-if="isset(item.changes)"
                      class="ma-0"
                      flat
                    >
                      <v-expansion-panel>
                        <v-expansion-panel-header class="px-0">
                          <span v-html="item.changes_title_label"></span>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content class="px-0">
                          <div v-html="item.changes_label"></div>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                    <div v-else class="caption grey--text">—</div>
                  </v-card-text>
                </v-card>
              </div>
            </div>
          </div>
          <div
            v-else
            class="text-center caption grey--text py-8"
          >
            {{ $t("no_data_found") }}
          </div>
          <div
            class="d-flex flex-wrap align-center justify-end pa-2 mt-2"
            v-if="!loading && totalItems > 0"
          >
            <v-select
              v-model="itemsPerPage"
              :items="dataTableOptions"
              dense
              hide-details
              class="pt-0 mt-0 mr-4"
              style="max-width: 88px"
              @change="onTimelineItemsPerPageChange"
            />
            <v-pagination
              v-model="currentPage"
              :length="timelinePageCount"
              :total-visible="$vuetify.breakpoint.smAndDown ? 5 : 7"
              @input="getHistory"
            />
          </div>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <span @click="close">
          <CloseButton />
        </span>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import { API_BASE_URL, DEFAULT_ITEMS_PER_PAGE_OPTIONS } from "@/config";
import axios from "axios";
import CloseButton from "@/components/Buttons/CloseButton.vue";

export default {
  name: "HistoryTable",
  components: { CloseButton },
  emits: ["input"],
  props: {
    model_id: {
      default: null,
    },
    model: {
      default: null,
    },
    value: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      historyView: "table",
      items: [],
      dataTableOptions: DEFAULT_ITEMS_PER_PAGE_OPTIONS,
      loading: false,
      currentPage: 1,
      itemsPerPage: DEFAULT_ITEMS_PER_PAGE_OPTIONS[0],
      totalItems: 0,
      headers: [
        {
          text: this.$t("srno"),
          sortable: false,
          value: "serial",
        },
        { text: this.translate("user"), value: "user_label", sortable: false },
        {
          text: this.translate("operation"),
          value: "operation_label",
          sortable: false,
        },
        { text: this.translate("date"), value: "date_label", sortable: false },
        {
          text: this.translate("changes"),
          value: "changes_label",
          sortable: false,
        },
      ],
      filters: {
        from: null,
        to: null,
      },
      axiosOptions: {
        headers: {
          Authorization: "Bearer " + this.$store.state.AccessToken,
        },
      },
    };
  },
  watch: {
    value(val) {
      this.items = [];
      this.currentPage = 1;
      this.historyView = "table";

      if (val) {
        this.getHistory();
      }
    },
  },
  mounted() {
    this.getHistory();
  },
  computed: {
    activeModelId() {
      if (this.model_id === 0) return true;

      return this.model_id;
    },
    timelinePageCount() {
      const n = Math.ceil(this.totalItems / this.itemsPerPage);
      return n > 0 ? n : 1;
    },
  },
  methods: {
    close() {
      this.$emit("input", false);
    },
    getHistory() {
      this.loading = true;
      axios
        .get(this.url(), this.axiosOptions)
        .finally(() => {
          this.loading = false;
        })
        .then(({ data }) => {
          const response = data.data;
          this.totalItems = data?.total || 0;
          this.items = response.logs.map((item, index) => {
            item.serial =
              (this.currentPage - 1) * this.itemsPerPage + index + 1;
            item.date_label = this.formatDateWithLocale(item.created_at);
            item.changes_title_label = this.processLogChangesForTableView(
              item,
              true
            );
            // remove first element from changes, as its already used in title
            delete item.changes[Object.keys(item.changes)[0]];
            item.changes_label = this.processLogChangesForTableView(item);
            return item;
          });
          // this.headers = response.table_columns
        })
        .catch((error) => {
          console.log(error);
        });
    },
    url() {
      let url =
        API_BASE_URL +
        "/user_action_history?model_id=" +
        this.model_id +
        "&model=" +
        this.model +
        "&page=" +
        this.currentPage +
        "&per_page=" +
        this.itemsPerPage;

      for (const filterKey in this.filters) {
        if (this.filters[filterKey]) {
          url += "&" + filterKey + "=" + this.filters[filterKey];
        }
      }

      return url;
    },
    processLogChangesForTableView(log, title = false) {
      // concat object to string with key value pair
      let changes = log.changes;
      let stringValue = "";
      let lastChangesKey = Object.keys(changes).pop();
      for (const key in changes) {
        let current = changes[key];
        if (key === "color") {
          current["old"] = current["old"]
            ? `<input style="height: 20px!important;" type="color" value="#${current["old"]}" disabled>`
            : "";
          // use html color swatch
          current["new"] = current["new"]
            ? `<input style="height: 20px!important;" type="color" value="#${current["new"]}" disabled>`
            : "";
        }

        let from = current["old"]
          ? `<strong>From</strong> ${current["old"]}`
          : "";
        let conjuction = current["old"] && current["new"] ? " to " : "";
        stringValue += `<strong>${
          current["label"]
        }</strong>: ${from} ${conjuction} ${current["new"] || "-"}`;

        if (key !== lastChangesKey) {
          stringValue += "<br>";
        }

        if (title) {
          break;
        }
      }

      return stringValue;
    },
    onPagination(pagination) {
      this.currentPage = pagination.page;
      this.itemsPerPage = pagination.itemsPerPage;
      this.getHistory();
    },
    onTimelineItemsPerPageChange() {
      this.currentPage = 1;
      this.getHistory();
    },
    translate(key) {
      return this.$t("user_action_history_section." + key);
    },
  },
};
</script>

<style scoped>
.horizontal-history-timeline__step {
  width: 272px;
  flex-shrink: 0;
}

.horizontal-history-timeline__axis {
  min-height: 20px;
}

.horizontal-history-timeline__line {
  border-top: 2px solid rgba(0, 0, 0, 0.12);
  min-width: 16px;
  height: 0;
  align-self: center;
}

.theme--dark .horizontal-history-timeline__line {
  border-top-color: rgba(255, 255, 255, 0.2);
}
</style>
